import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  CreditCard,
  ChevronDown,
  Blocks,
  Menu,
} from "lucide-react";
import Sidebar from "../components/Sidebar";

const getAccessToken = () =>
  sessionStorage.getItem("access_token");

export default function Dashboard() {
  const navigate = useNavigate();

  const [bookings, setBookings] = useState([]);
  const [cancelledBookings, setCancelledBookings] =
    useState([]);
  const [resources, setResources] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [selectedResource, setSelectedResource] =
    useState("all");
  const [cancelledFilter, setCancelledFilter] =
    useState("week");
  const [selectedPeriod, setSelectedPeriod] =
    useState("this_week");
  const [statusFilter, setStatusFilter] =
    useState("all");
  const [mobileSidebarOpen, setMobileSidebarOpen] =
    useState(false);

  const API_BASE_URL =
    "https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/bookings";
    
  const CALENDAR_ENDPOINT =
    `${API_BASE_URL}/viewAllbookingmonth`;

  const RESOURCE_API_BASE_URL =
    "https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/resource";

  const PLANS_API_BASE =
    "https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/plans";

  const formatTime = (dateString) => {
    if (!dateString) return "N/A";

    try {
      const date = new Date(dateString);

      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: "Asia/Jakarta",
      });
    } catch (e) {
      return "N/A";
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case "confirmed":
        return "bg-green-100 text-green-800";

      case "cancelled":
      case "canceled":
        return "bg-red-100 text-red-800";

      case "tentative":
      case "pending":
        return "bg-yellow-100 text-yellow-800";

      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const fetchAuth0Users = async () => {
    const userMap = new Map();

    try {
      const storedUser = JSON.parse(
        sessionStorage.getItem("user") || "{}"
      );

      if (storedUser.email) {
        const username =
          storedUser.username ||
          storedUser.name ||
          storedUser.email.split("@")[0];

        userMap.set(storedUser.email, username);
      }
    } catch (e) {
      console.error(
        "Error reading user from sessionStorage:",
        e
      );
    }

    return userMap;
  };

  const fetchBookings = async () => {
    try {
      const token = getAccessToken();

      if (!token) {
        throw new Error("Access token not found");
      }

      const now = new Date();

      const startDate = new Date(
        now.getFullYear(),
        now.getMonth() - 1,
        1,
        0,
        0,
        0,
        0
      );

      const endDate = new Date(
        now.getFullYear(),
        now.getMonth() + 1,
        0,
        23,
        59,
        59,
        999
      );

      const initialdate = startDate.toISOString();
      const finaldate = endDate.toISOString();

      const fetchPage = async (page) => {
        const params = new URLSearchParams({
          count: "0",
          initialdate,
          finaldate,
          page: String(page),
          limit: "100",
        });

        const response = await fetch(
          `${CALENDAR_ENDPOINT}?${params.toString()}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const errorText = await response
            .text()
            .catch(() => "");

          throw new Error(
            `Failed to fetch bookings: ${response.status} ${errorText}`
          );
        }

        return response.json();
      };

      const firstPage = await fetchPage(1);

      const firstData = Array.isArray(firstPage)
        ? firstPage
        : firstPage?.data || [];

      const totalPages =
        Number(firstPage?.meta?.totalPages) || 1;

      let allBookings = [...firstData];

      console.log("Dashboard first booking page:", {
        bookings: firstData.length,
        totalPages,
        initialdate,
        finaldate,
      });

      if (totalPages > 1) {
        const remainingPages = await Promise.all(
          Array.from(
            { length: totalPages - 1 },
            (_, index) =>
              fetchPage(index + 2)
          )
        );

        remainingPages.forEach((pageResult) => {
          const pageData = Array.isArray(
            pageResult
          )
            ? pageResult
            : pageResult?.data || [];

          allBookings.push(...pageData);
        });
      }

      console.log(
        `Dashboard: fetched ${allBookings.length} bookings across ${totalPages} page(s)`
      );

      return allBookings;
    } catch (error) {
      console.error(
        "Error fetching dashboard bookings:",
        error
      );

      return [];
    }
  };

  const fetchResources = async () => {
    try {
      const response = await fetch(
        `${RESOURCE_API_BASE_URL}/viewAllResource`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAccessToken()}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          "Failed to fetch resources"
        );
      }

      const resourcesData =
        await response.json();

      console.log(
        "Resources fetched:",
        resourcesData
      );

      return Array.isArray(resourcesData)
        ? resourcesData
        : resourcesData?.data || [];
    } catch (error) {
      console.error(
        "Error fetching resources:",
        error
      );

      return [];
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await fetch(
        `${PLANS_API_BASE}/getPlan`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAccessToken()}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({}));

        throw new Error(
          errorData.message ||
            "Failed to fetch plans"
        );
      }

      const data = await response.json();

      console.log(
        "Plans fetched:",
        data
      );

      return Array.isArray(data?.plans)
        ? data.plans
        : [];
    } catch (error) {
      console.error(
        "Error fetching plans:",
        error
      );

      return [];
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const userMap =
          await fetchAuth0Users();

        const [
          bookingsData,
          resourcesData,
          plansData,
        ] = await Promise.all([
          fetchBookings(),
          fetchResources(),
          fetchPlans(),
        ]);

        const calculateDuration = (
          start,
          end
        ) => {
          if (!start || !end) {
            return "N/A";
          }

          try {
            const startDate =
              new Date(start);

            const endDate =
              new Date(end);

            const diff = Math.abs(
              endDate.getTime() -
                startDate.getTime()
            );

            const hours = Math.floor(
              diff /
                (1000 * 60 * 60)
            );

            const minutes = Math.floor(
              (diff %
                (1000 * 60 * 60)) /
                (1000 * 60)
            );

            if (hours > 0) {
              return `${hours} hour${
                hours > 1 ? "s" : ""
              }`;
            }

            return `${minutes} min`;
          } catch (e) {
            return "N/A";
          }
        };

        const resourceMap = new Map(
          (resourcesData || []).map(
            (resource) => [
              String(resource.id),
              resource.name,
            ]
          )
        );

        const transformBooking = (
          booking
        ) => {
          let email =
            booking.customer_id ||
            booking.customer_name ||
            booking.metadata
              ?.customer_name;

          if (
            booking.customer_name &&
            booking.customer_name.includes(
              "@"
            )
          ) {
            email =
              booking.customer_name;
          } else if (
            booking.metadata
              ?.customer_name &&
            booking.metadata.customer_name.includes(
              "@"
            )
          ) {
            email =
              booking.metadata
                .customer_name;
          }

          const userName =
            email &&
            userMap.has(email)
              ? userMap.get(email)
              : booking.metadata
                  ?.customer_name ||
                booking.customer_name ||
                "N/A";

          const resourceId =
            booking.resource?.id ||
            booking.resource_id ||
            null;

          const resourceName =
            booking.resource?.name ||
            (resourceId
              ? resourceMap.get(
                  String(resourceId)
                )
              : undefined) ||
            "N/A";

          const backendStatus =
            booking.status
              ?.toString()
              .toLowerCase();

          const isCancelled =
            Boolean(
              booking.is_canceled
            ) ||
            backendStatus ===
              "cancelled" ||
            backendStatus ===
              "canceled";

          let status;

          if (isCancelled) {
            status = "Cancelled";
          } else if (
            booking.is_temporary
          ) {
            status = "Tentative";
          } else {
            status = "Confirmed";
          }

          return {
            id: booking.id,

            customer: userName,

            resource: resourceId
              ? {
                  id: resourceId,
                  name: resourceName,
                }
              : {
                  id: null,
                  name: resourceName,
                },

            service: booking.service
              ? {
                  id: booking.service.id,
                  name: booking.service.name,
                }
              : undefined,

            starts_at:
              booking.starts_at,

            ends_at:
              booking.ends_at,

            canceled_at:
              booking.canceled_at,

            duration:
              calculateDuration(
                booking.starts_at,
                booking.ends_at
              ),

            status,

            is_canceled:
              isCancelled,

            is_temporary:
              Boolean(
                booking.is_temporary
              ),

            location:
              booking.location
                ?.name ||
              booking.location_id ||
              "N/A",
          };
        };

        const allTransformedBookings =
          (bookingsData || []).map(
            transformBooking
          );

        const cancelled = [];
        const active = [];

        allTransformedBookings.forEach(
          (booking) => {
            if (
              booking.is_canceled
            ) {
              cancelled.push({
                ...booking,
                status: "Cancelled",
                is_canceled: true,
              });
            } else {
              active.push(booking);
            }
          }
        );

        console.log(
          `Dashboard: Active bookings: ${active.length}, Cancelled: ${cancelled.length}`
        );

        console.log(
          "Dashboard cancelled bookings:",
          cancelled
        );

        setBookings(active);
        setCancelledBookings(
          cancelled
        );
        setResources(
          resourcesData || []
        );
        setPlans(
          plansData || []
        );
      } catch (err) {
        console.error(
          "Dashboard data error:",
          err
        );

        setError(
          "Failed to fetch data"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  
  const getThisMonthCancelledCount =
    () => {
      const now = new Date();

      const currentMonth =
        now.getMonth();

      const currentYear =
        now.getFullYear();

      return cancelledBookings.filter(
        (booking) => {
          if (
            !booking.is_canceled ||
            !booking.starts_at
          ) {
            return false;
          }

          const bookingDate =
            new Date(
              booking.starts_at
            );

          if (
            Number.isNaN(
              bookingDate.getTime()
            )
          ) {
            return false;
          }

          return (
            bookingDate.getMonth() ===
              currentMonth &&
            bookingDate.getFullYear() ===
              currentYear
          );
        }
      ).length;
    };

  const getCurrentWeekDates =
    () => {
      const today = new Date();

      const currentDay =
        today.getDay();

      
      const daysFromMonday =
        currentDay === 0
          ? 6
          : currentDay - 1;

      const monday =
        new Date(today);

      monday.setDate(
        today.getDate() -
          daysFromMonday
      );

      monday.setHours(
        0,
        0,
        0,
        0
      );

      const weekDates = [];

      for (
        let i = 0;
        i < 7;
        i++
      ) {
        const date =
          new Date(monday);

        date.setDate(
          monday.getDate() + i
        );

        weekDates.push(date);
      }

      return weekDates;
    };

  const getCurrentMonth =
    () => {
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];

      return monthNames[
        new Date().getMonth()
      ];
    };

  const getCurrentMonthBookings =
    () => {
      const currentMonth =
        new Date().getMonth();

      const currentYear =
        new Date().getFullYear();

      return bookings.filter(
        (booking) => {
          if (
            !booking.starts_at
          ) {
            return false;
          }

          const bookingDate =
            new Date(
              booking.starts_at
            );

          return (
            bookingDate.getMonth() ===
              currentMonth &&
            bookingDate.getFullYear() ===
              currentYear &&
            (booking.status ===
              "Confirmed" ||
              booking.status ===
                "Tentative")
          );
        }
      ).length;
    };

  const totalResources =
    resources.length;

  const totalPlans =
    plans.length;

  const currentMonthConfirmedBookings =
    getCurrentMonthBookings();

  const generateChartData =
    () => {
      const weekDates =
        getCurrentWeekDates();

      return weekDates.map(
        (date) => {
          const dateStr = date
            .toISOString()
            .split("T")[0];

          let count = 0;

          if (
            selectedResource ===
            "all"
          ) {
            count =
              bookings.filter(
                (booking) => {
                  if (
                    !booking.starts_at
                  ) {
                    return false;
                  }

                  const bookingDate =
                    new Date(
                      booking.starts_at
                    )
                      .toISOString()
                      .split(
                        "T"
                      )[0];

                  return (
                    bookingDate ===
                    dateStr
                  );
                }
              ).length;
          } else {
            count =
              bookings.filter(
                (booking) => {
                  if (
                    !booking.starts_at
                  ) {
                    return false;
                  }

                  const bookingDate =
                    new Date(
                      booking.starts_at
                    )
                      .toISOString()
                      .split(
                        "T"
                      )[0];

                  return (
                    bookingDate ===
                      dateStr &&
                    String(
                      booking
                        .resource
                        ?.id
                    ) ===
                      String(
                        selectedResource
                      )
                  );
                }
              ).length;
          }

          return {
            date,

            label:
              date.toLocaleDateString(
                "en-US",
                {
                  month:
                    "short",
                  day: "numeric",
                }
              ),

            value: count,
          };
        }
      );
    };

  const chartData =
    generateChartData();

  const dataValues =
    chartData.map(
      (data) => data.value
    );

  const maxDataValue =
    Math.max(
      ...dataValues,
      0
    );

  let yAxisMax;

  if (
    maxDataValue === 0
  ) {
    yAxisMax = 4;
  } else if (
    maxDataValue <= 3
  ) {
    yAxisMax = 6;
  } else if (
    maxDataValue <= 10
  ) {
    yAxisMax =
      Math.ceil(
        maxDataValue * 1.5
      );
  } else {
    yAxisMax =
      Math.ceil(
        maxDataValue * 1.3
      );
  }

  const yAxisMin = 0;

  const yAxisRange =
    yAxisMax - yAxisMin;

  const yAxisStep =
    yAxisRange / 3;

  const yAxisLabels = [
    Math.round(yAxisMax),

    Math.round(
      yAxisMax - yAxisStep
    ),

    Math.round(
      yAxisMax -
        yAxisStep * 2
    ),

    yAxisMin,
  ];

  const filteredBookings =
    bookings
      .filter((booking) => {
        let matchesPeriod = true;
        let matchesStatus = true;

        if (
          selectedPeriod &&
          booking.starts_at
        ) {
          const bookingDate =
            new Date(
              booking.starts_at
            );

          const now = new Date();

          if (
            selectedPeriod ===
            "this_week"
          ) {
            const startOfWeek =
              new Date(now);

            const day =
              startOfWeek.getDay();

            const diff =
              startOfWeek.getDate() -
              day +
              (day === 0
                ? -6
                : 1);

            startOfWeek.setDate(
              diff
            );

            startOfWeek.setHours(
              0,
              0,
              0,
              0
            );

            const endOfWeek =
              new Date(
                startOfWeek
              );

            endOfWeek.setDate(
              startOfWeek.getDate() +
                6
            );

            endOfWeek.setHours(
              23,
              59,
              59,
              999
            );

            matchesPeriod =
              bookingDate >=
                startOfWeek &&
              bookingDate <=
                endOfWeek;
          } else if (
            selectedPeriod ===
            "last_week"
          ) {
            const startOfLastWeek =
              new Date(now);

            const day =
              startOfLastWeek.getDay();

            const diff =
              startOfLastWeek.getDate() -
              day +
              (day === 0
                ? -6
                : 1) -
              7;

            startOfLastWeek.setDate(
              diff
            );

            startOfLastWeek.setHours(
              0,
              0,
              0,
              0
            );

            const endOfLastWeek =
              new Date(
                startOfLastWeek
              );

            endOfLastWeek.setDate(
              startOfLastWeek.getDate() +
                6
            );

            endOfLastWeek.setHours(
              23,
              59,
              59,
              999
            );

            matchesPeriod =
              bookingDate >=
                startOfLastWeek &&
              bookingDate <=
                endOfLastWeek;
          } else if (
            selectedPeriod ===
            "this_month"
          ) {
            const startOfMonth =
              new Date(
                now.getFullYear(),
                now.getMonth(),
                1
              );

            const endOfMonth =
              new Date(
                now.getFullYear(),
                now.getMonth() +
                  1,
                0,
                23,
                59,
                59,
                999
              );

            matchesPeriod =
              bookingDate >=
                startOfMonth &&
              bookingDate <=
                endOfMonth;
          } else if (
            selectedPeriod ===
            "last_month"
          ) {
            const startOfLastMonth =
              new Date(
                now.getFullYear(),
                now.getMonth() -
                  1,
                1
              );

            const endOfLastMonth =
              new Date(
                now.getFullYear(),
                now.getMonth(),
                0,
                23,
                59,
                59,
                999
              );

            matchesPeriod =
              bookingDate >=
                startOfLastMonth &&
              bookingDate <=
                endOfLastMonth;
          }
        }

        if (
          statusFilter !==
          "all"
        ) {
          const bookingStatus =
            booking.status.toLowerCase();

          if (
            statusFilter ===
            "tentative"
          ) {
            matchesStatus =
              bookingStatus ===
                "tentative" ||
              bookingStatus ===
                "pending";
          } else if (
            statusFilter ===
            "approved"
          ) {
            matchesStatus =
              bookingStatus ===
              "confirmed";
          }
        }

        return (
          matchesPeriod &&
          matchesStatus
        );
      })
      .sort(
        (a, b) =>
          new Date(
            b.starts_at
          ) -
          new Date(
            a.starts_at
          )
      );

  const recentBookings =
    filteredBookings.slice(
      0,
      6
    );

  /*
   * Current date.
   */
  const today = new Date();

  const todayStart =
    new Date(today);

  todayStart.setHours(
    0,
    0,
    0,
    0
  );

  const todayEnd =
    new Date(today);

  todayEnd.setHours(
    23,
    59,
    59,
    999
  );

 
  const currentDay =
    today.getDay();

  const daysFromMonday =
    currentDay === 0
      ? 6
      : currentDay - 1;

  const startOfWeek =
    new Date(today);

  startOfWeek.setDate(
    today.getDate() -
      daysFromMonday
  );

  startOfWeek.setHours(
    0,
    0,
    0,
    0
  );

  const endOfWeek =
    new Date(
      startOfWeek
    );

  endOfWeek.setDate(
    startOfWeek.getDate() +
      6
  );

  endOfWeek.setHours(
    23,
    59,
    59,
    999
  );


  const filteredCancelledBookings =
    cancelledBookings
      .filter((booking) => {
        if (
          !booking.is_canceled ||
          !booking.starts_at
        ) {
          return false;
        }

        const bookingDate =
          new Date(
            booking.starts_at
          );

        if (
          Number.isNaN(
            bookingDate.getTime()
          )
        ) {
          return false;
        }

        if (
          cancelledFilter ===
          "today"
        ) {
          return (
            bookingDate >=
              todayStart &&
            bookingDate <=
              todayEnd
          );
        }

        return (
          bookingDate >=
            startOfWeek &&
          bookingDate <=
            endOfWeek
        );
      })
      .sort(
        (a, b) =>
          new Date(
            b.starts_at
          ) -
          new Date(
            a.starts_at
          )
      );

  if (loading) {
    return (
      <div className="flex h-screen overflow-hidden bg-gray-50">
        <Sidebar currentPath="/dashboard" />

        <div className="flex-1 flex flex-col overflow-hidden">
          <main className="flex-1 overflow-y-auto bg-gray-50 flex items-center justify-center">
            <div className="text-gray-500">
              Loading...
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen overflow-hidden bg-gray-50">
        <Sidebar currentPath="/dashboard" />

        <div className="flex-1 flex flex-col overflow-hidden">
          <main className="flex-1 overflow-y-auto bg-gray-50 flex items-center justify-center">
            <div className="text-red-500">
              {error}
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar
        currentPath="/dashboard"
        mobileOpen={
          mobileSidebarOpen
        }
        onMobileClose={() =>
          setMobileSidebarOpen(false)
        }
      />

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="md:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 sticky top-0 z-30">
          <button
            className="p-2 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
            onClick={() =>
              setMobileSidebarOpen(
                true
              )
            }
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>

          <img
            src="/logo.png"
            alt="Optimus Logo"
            className="h-7 w-auto object-contain mr-5"
          />
        </header>

        <main className="flex-1 overflow-y-auto bg-gray-50">
          <div className="p-4 md:p-6">
            <div className="mb-6">
              <h1 className="text-2xl font-semibold text-gray-900">
                Dashboard
              </h1>

              <p className="text-sm text-gray-500 mt-1">
                Overview of all of recent
                bookings, inventory and plans
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div
                onClick={() =>
                  navigate(
                    "/bookings"
                  )
                }
                className="bg-white rounded-lg p-5 shadow-sm border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="text-sm text-gray-600">
                    Recent Bookings
                  </div>

                  <div className="text-xs text-gray-400">
                    {getCurrentMonth()}
                  </div>
                </div>

                <div className="flex items-end justify-between">
                  <div className="text-3xl font-semibold text-gray-900">
                    {
                      currentMonthConfirmedBookings
                    }
                  </div>

                  <Calendar
                    className="w-5 h-5 text-gray-400"
                    strokeWidth={1.5}
                  />
                </div>
              </div>

              <div
                onClick={() =>
                  navigate(
                    "/resources"
                  )
                }
                className="bg-white rounded-lg p-5 shadow-sm border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="text-sm text-gray-600">
                    Total Resources
                  </div>
                </div>

                <div className="flex items-end justify-between">
                  <div className="text-3xl font-semibold text-gray-900">
                    {
                      totalResources
                    }
                  </div>

                  <Blocks
                    className="w-5 h-5 text-gray-400"
                    strokeWidth={1.5}
                  />
                </div>
              </div>

              <div
                onClick={() =>
                  navigate("/plans")
                }
                className="bg-white rounded-lg p-5 shadow-sm border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="text-sm text-gray-600">
                    Total Plans
                  </div>
                </div>

                <div className="flex items-end justify-between">
                  <div className="text-3xl font-semibold text-gray-900">
                    {totalPlans}
                  </div>

                  <CreditCard
                    className="w-5 h-5 text-gray-400"
                    strokeWidth={1.5}
                  />
                </div>
              </div>

              <div className="bg-[#214BAE] rounded-lg p-5 shadow-sm cursor-pointer hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm text-blue-100">
                    Cancelled Bookings
                  </div>

                  <span className="text-xs text-blue-100">
                    This month
                  </span>
                </div>

                <div className="flex items-end justify-between">
                  <div className="text-3xl font-semibold text-white">
                    {
                      getThisMonthCancelledCount()
                    }
                  </div>

                  <Calendar
                    className="w-5 h-5 text-white"
                    strokeWidth={1.5}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              <div className="lg:col-span-2">
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Resource Usage Overview
                    </h3>

                    <div className="relative">
                      <select
                        value={
                          selectedResource
                        }
                        onChange={(e) =>
                          setSelectedResource(
                            e.target.value
                          )
                        }
                        className="appearance-none text-sm border border-gray-200 rounded-lg px-4 py-2 pr-10 text-gray-700 bg-white cursor-pointer hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="all">
                          All Resources
                        </option>

                        {resources.map(
                          (resource) => (
                            <option
                              key={
                                resource.id ||
                                resource._id
                              }
                              value={
                                resource.id ||
                                resource._id
                              }
                            >
                              {
                                resource.name
                              }
                            </option>
                          )
                        )}
                      </select>

                      <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                    </div>
                  </div>

                  <div
                    className="relative"
                    style={{
                      height: "260px",
                      paddingLeft:
                        "40px",
                      paddingBottom:
                        "32px",
                    }}
                  >
                    <div className="absolute left-0 top-0 bottom-8 flex flex-col justify-between text-sm text-gray-500 font-medium">
                      {yAxisLabels.map(
                        (label, i) => (
                          <span
                            key={i}
                          >
                            {
                              label
                            }
                          </span>
                        )
                      )}
                    </div>

                    <div
                      className="h-full relative"
                      style={{
                        paddingBottom:
                          "32px",
                      }}
                    >
                      <div className="absolute inset-0 bottom-8 flex flex-col justify-between">
                        {yAxisLabels.map(
                          (val, i) => (
                            <div
                              key={i}
                              className="border-t border-dashed border-gray-200"
                            />
                          )
                        )}
                      </div>

                      {hoveredPoint && (
                        <div
                          className="absolute bg-gray-900 text-white text-xs rounded px-2 py-1 pointer-events-none z-10"
                          style={{
                            left: `${hoveredPoint.x}%`,
                            top: `${hoveredPoint.y}%`,
                            transform:
                              "translate(-50%, -120%)",
                          }}
                        >
                          <div className="font-semibold">
                            {
                              hoveredPoint.label
                            }
                          </div>

                          <div>
                            {
                              hoveredPoint.value
                            }{" "}
                            booking
                            {hoveredPoint.value !==
                            1
                              ? "s"
                              : ""}
                          </div>
                        </div>
                      )}

                      <svg
                        className="absolute inset-0 w-full bottom-8"
                        style={{
                          height:
                            "calc(100% - 32px)",
                        }}
                        viewBox="0 0 100 100"
                        preserveAspectRatio="none"
                      >
                        <defs>
                          <linearGradient
                            id="lineGradient"
                            x1="0%"
                            y1="0%"
                            x2="100%"
                            y2="0%"
                          >
                            <stop
                              offset="0%"
                              stopColor="#5b8def"
                            />

                            <stop
                              offset="100%"
                              stopColor="#5b8def"
                            />
                          </linearGradient>
                        </defs>

                        <path
                          fill="none"
                          stroke="url(#lineGradient)"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          vectorEffect="non-scaling-stroke"
                          d={(() => {
                            if (
                              chartData.length ===
                              0
                            ) {
                              return "";
                            }

                            const points =
                              chartData.map(
                                (
                                  item,
                                  i
                                ) => {
                                  const x =
                                    chartData.length ===
                                    1
                                      ? 50
                                      : (i /
                                          (chartData.length -
                                            1)) *
                                        100;

                                  const y =
                                    yAxisRange >
                                    0
                                      ? ((yAxisMax -
                                          item.value) /
                                          yAxisRange) *
                                        100
                                      : 50;

                                  return {
                                    x,
                                    y,
                                  };
                                }
                              );

                            if (
                              points.length ===
                              1
                            ) {
                              return `M ${points[0].x},${points[0].y}`;
                            }

                            if (
                              points.length ===
                              2
                            ) {
                              return `M ${points[0].x},${points[0].y} L ${points[1].x},${points[1].y}`;
                            }

                            let path = `M ${points[0].x},${points[0].y}`;

                            for (
                              let i = 0;
                              i <
                              points.length -
                                1;
                              i++
                            ) {
                              const p0 =
                                points[
                                  Math.max(
                                    0,
                                    i - 1
                                  )
                                ];

                              const p1 =
                                points[i];

                              const p2 =
                                points[
                                  i + 1
                                ];

                              const p3 =
                                points[
                                  Math.min(
                                    points.length -
                                      1,
                                    i + 2
                                  )
                                ];

                              const cp1x =
                                p1.x +
                                (p2.x -
                                  p0.x) /
                                  6;

                              const cp1y =
                                p1.y +
                                (p2.y -
                                  p0.y) /
                                  6;

                              const cp2x =
                                p2.x -
                                (p3.x -
                                  p1.x) /
                                  6;

                              const cp2y =
                                p2.y -
                                (p3.y -
                                  p1.y) /
                                  6;

                              path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
                            }

                            return path;
                          })()}
                        />

                        {chartData.map(
                          (item, i) => {
                            const x =
                              chartData.length ===
                              1
                                ? 50
                                : (i /
                                    (chartData.length -
                                      1)) *
                                  100;

                            const y =
                              yAxisRange >
                              0
                                ? ((yAxisMax -
                                    item.value) /
                                    yAxisRange) *
                                  100
                                : 50;

                            return (
                              <circle
                                key={i}
                                cx={x}
                                cy={y}
                                r="2"
                                fill="transparent"
                                strokeWidth="10"
                                stroke="transparent"
                                className="cursor-pointer"
                                style={{
                                  pointerEvents:
                                    "all",
                                }}
                                onMouseEnter={() =>
                                  setHoveredPoint(
                                    {
                                      x,
                                      y,
                                      label:
                                        item.label,
                                      value:
                                        item.value,
                                    }
                                  )
                                }
                                onMouseLeave={() =>
                                  setHoveredPoint(
                                    null
                                  )
                                }
                              />
                            );
                          }
                        )}
                      </svg>

                      <div className="absolute bottom-0 left-0 right-0 flex justify-between text-sm text-gray-500 font-medium">
                        {chartData.map(
                          (item, i) => (
                            <span
                              key={i}
                              className="text-center"
                            >
                              {
                                item.label
                              }
                            </span>
                          )
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-600">
                      <span className="font-semibold">
                        Peak Usage:{" "}
                      </span>

                      {(() => {
                        const maxBooking =
                          Math.max(
                            ...chartData.map(
                              (d) =>
                                d.value
                            )
                          );

                        const peakDays =
                          chartData.filter(
                            (d) =>
                              d.value ===
                              maxBooking
                          );

                        const resourceName =
                          selectedResource ===
                          "all"
                            ? "All Resources"
                            : resources.find(
                                (r) =>
                                  String(
                                    r.id ||
                                      r._id
                                  ) ===
                                  String(
                                    selectedResource
                                  )
                              )?.name ||
                              "Selected Resource";

                        if (
                          maxBooking ===
                          0
                        ) {
                          return `No bookings for ${resourceName} this week.`;
                        }

                        return `${maxBooking} booking${
                          maxBooking !==
                          1
                            ? "s"
                            : ""
                        } for ${resourceName} on ${peakDays
                          .map(
                            (d) =>
                              d.label
                          )
                          .join(
                            ", "
                          )} (Current Week)`;
                      })()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 flex flex-col">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Cancelled Bookings
                  </h3>

                  <div className="relative">
                    <select
                      value={
                        cancelledFilter
                      }
                      onChange={(e) =>
                        setCancelledFilter(
                          e.target.value
                        )
                      }
                      className="appearance-none text-sm border border-gray-200 rounded-md px-2.5 py-1 pr-7 text-gray-600 bg-white cursor-pointer hover:border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="today">
                        Today
                      </option>

                      <option value="week">
                        This Week
                      </option>
                    </select>

                    <ChevronDown className="w-3 h-3 text-gray-400 absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                  </div>
                </div>

                <div
                  className="space-y-3 flex-1 overflow-y-auto"
                  style={{
                    maxHeight:
                      "280px",
                  }}
                >
                  {filteredCancelledBookings.length >
                  0 ? (
                    filteredCancelledBookings.map(
                      (booking) => (
                        <div
                          key={
                            booking.id
                          }
                          className="border border-gray-200 rounded-lg p-3.5 flex justify-between items-start"
                        >
                          <div>
                            <div className="text-sm font-semibold text-gray-900 mb-1">
                              {
                                booking
                                  .resource
                                  ?.name
                              }
                            </div>

                            <div className="text-xs text-gray-600 font-medium">
                              {
                                booking.customer
                              }
                            </div>
                          </div>

                          <div className="text-right text-sm text-gray-500">
                            <div>
                              {booking.starts_at
                                ? new Date(
                                    booking.starts_at
                                  ).toLocaleDateString(
                                    "en-US",
                                    {
                                      weekday:
                                        "short",
                                      day: "numeric",
                                      month:
                                        "short",
                                      year:
                                        "numeric",
                                    }
                                  )
                                : "N/A"}
                            </div>

                            <div className="font-medium">
                              {formatTime(
                                booking.starts_at
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    )
                  ) : (
                    <div className="text-center text-sm text-gray-500 py-8">
                      No cancelled bookings
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Recent Bookings
                  </h3>

                  <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                    <div className="relative">
                      <select
                        value={
                          selectedPeriod
                        }
                        onChange={(e) =>
                          setSelectedPeriod(
                            e.target.value
                          )
                        }
                        className="appearance-none text-sm border border-gray-200 rounded-md px-3 py-1.5 pr-8 text-gray-700 bg-white cursor-pointer hover:border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        style={{
                          minWidth:
                            "140px",
                        }}
                      >
                        <option value="this_week">
                          This Week
                        </option>

                        <option value="last_week">
                          Last Week
                        </option>

                        <option value="this_month">
                          This Month
                        </option>

                        <option value="last_month">
                          Last Month
                        </option>
                      </select>

                      <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-2.5 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                    </div>

                    <div className="relative">
                      <select
                        value={
                          statusFilter
                        }
                        onChange={(e) =>
                          setStatusFilter(
                            e.target.value
                          )
                        }
                        className="appearance-none text-sm border border-gray-200 rounded-md px-3 py-1.5 pr-8 text-gray-700 bg-white cursor-pointer hover:border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        style={{
                          minWidth:
                            "120px",
                        }}
                      >
                        <option value="all">
                          Status
                        </option>

                        <option value="approved">
                          Approved
                        </option>

                        <option value="tentative">
                          Tentative
                        </option>
                      </select>

                      <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-2.5 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        User Name
                      </th>

                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Resource
                      </th>

                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Date
                      </th>

                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Start Time
                      </th>

                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        End Time
                      </th>

                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Duration
                      </th>

                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                    </tr>
                  </thead>

                  <tbody className="bg-white divide-y divide-gray-200">
                    {recentBookings.length >
                    0 ? (
                      recentBookings.map(
                        (booking) => (
                          <tr
                            key={
                              booking.id
                            }
                            className="hover:bg-gray-50 transition-colors"
                          >
                            <td className="px-6 py-3.5 text-sm text-gray-900 font-medium">
                              {
                                booking.customer
                              }
                            </td>

                            <td className="px-6 py-3.5 text-sm text-gray-900">
                              {
                                booking
                                  .resource
                                  ?.name
                              }
                            </td>

                            <td className="px-6 py-3.5 text-sm text-gray-900">
                              {booking.starts_at
                                ? new Date(
                                    booking.starts_at
                                  ).toLocaleDateString(
                                    "en-US",
                                    {
                                      day: "numeric",
                                      month:
                                        "short",
                                      year:
                                        "numeric",
                                    }
                                  )
                                : "N/A"}
                            </td>

                            <td className="px-6 py-3.5 text-sm text-gray-900">
                              {formatTime(
                                booking.starts_at
                              )}
                            </td>

                            <td className="px-6 py-3.5 text-sm text-gray-900">
                              {formatTime(
                                booking.ends_at
                              )}
                            </td>

                            <td className="px-6 py-3.5 text-sm text-gray-900">
                              {
                                booking.duration
                              }
                            </td>

                            <td className="px-6 py-3.5">
                              <span
                                className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${getStatusBadgeClass(
                                  booking.status
                                )}`}
                              >
                                {
                                  booking.status
                                }
                              </span>
                            </td>
                          </tr>
                        )
                      )
                    ) : (
                      <tr>
                        <td
                          colSpan="7"
                          className="px-6 py-8 text-center text-sm text-gray-500"
                        >
                          No bookings found
                          for the selected
                          filters
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
